import { useStorage } from '@vueuse/core';

import { parseJsonFromStorage } from '@/helpers/parseJsonFromStorage';
import { DividaResource } from '@/models/resources';
import { IOrdenacaoContext } from './ordering/interfaces/IOrdenacaoContext';
import { IOrdemSalvaOrderingService } from './ordering/interfaces/IOrdemSalvaOrderingService';
import { LotesOrdenacaoCache } from './ordering/types/LotesOrdenacaoCache';
import { TipoOrdenacao } from './ordering/types/TipoOrdenacao';
import { DividasOrderingContext } from './ordering/DividasOrderingContext';
import storageService from './StorageService';
import sessionService from './SessionService';

export class DividasOrderingService implements IOrdemSalvaOrderingService {
  private dividasOrder: ReturnType<typeof useStorage<string[]>>;
  private orderTimestamp: ReturnType<typeof useStorage<number>>;
  private lotesOrdenacaoCache: ReturnType<typeof useStorage<LotesOrdenacaoCache | null>>;
  private currentUserHash: string | null = null;
  private readonly VALIDITY_DURATION = Number(
    appSettings.VUE_APP_DIVIDAS_ORDER_EXPIRATION_MS,
  ) || Number(appSettings.VUE_APP_VUE_QUERY_STALE_TIME) || 30 * 1000; // ms

  private orderingContext: IOrdenacaoContext | null = null;

  constructor() {
    // Inicializa com chaves padrão, serão atualizadas no primeiro uso
    this.dividasOrder = useStorage<string[]>(
      'dividas-order',
      [],
      sessionStorage,
      { mergeDefaults: true },
    );
    this.orderTimestamp = useStorage<number>(
      'dividas-order-timestamp',
      0,
      sessionStorage,
      { mergeDefaults: true },
    );
    this.lotesOrdenacaoCache = useStorage<LotesOrdenacaoCache | null>(
      'dividas-lotes-ordenacao',
      null,
      sessionStorage,
      { mergeDefaults: true },
    );
    this.ensureStorageReferences();
  }

  /**
   * Obtém o contexto de ordenação (lazy initialization para evitar ciclo de dependência)
   */
  private getOrderingContext(): IOrdenacaoContext {
    this.orderingContext ??= new DividasOrderingContext(this);
    return this.orderingContext;
  }

  /**
   * Inicializa as referências de storage com base no usuário atual
   */
  private ensureStorageReferences() {
    const userHash = DividasOrderingService.getUserHash();
    if (userHash !== this.currentUserHash) {
      this.updateStorageReferences(userHash);
    }
  }

  /**
   * Obtém o hash do usuário atual
   */
  private static getUserHash(): string | null {
    try {
      return sessionService.token?.payload.hash || storageService.hash.get() || null;
    } catch {
      return storageService.hash.get() || null;
    }
  }

  /**
   * Gera chaves únicas para o usuário atual
   */
  getUserSpecificKeys() {
    const userHash = this.currentUserHash || DividasOrderingService.getUserHash();
    const suffix = userHash ? `-${userHash}` : '';
    return {
      orderKey: `dividas-order${suffix}`,
      timestampKey: `dividas-order-timestamp${suffix}`,
      lotesKey: `dividas-lotes-ordenacao${suffix}`,
    };
  }

  /**
   * Atualiza as referências do storage com chaves específicas do usuário
   */
  private updateStorageReferences(newUserHash?: string | null) {
    const userHash = newUserHash !== undefined ? newUserHash : DividasOrderingService.getUserHash();
    if (userHash !== this.currentUserHash) {
      this.currentUserHash = userHash;
      const keys = this.getUserSpecificKeys();
      this.dividasOrder = useStorage<string[]>(
        keys.orderKey,
        [],
        sessionStorage,
        { mergeDefaults: true },
      );
      this.orderTimestamp = useStorage<number>(
        keys.timestampKey,
        0,
        sessionStorage,
        { mergeDefaults: true },
      );
      this.lotesOrdenacaoCache = useStorage<LotesOrdenacaoCache | null>(
        keys.lotesKey,
        null,
        sessionStorage,
        { mergeDefaults: true },
      );
    }
  }

  /**
   * Calcula a chave única para uma dívida baseada na API e ID
   */
  static calcularKey(api: string, id: number): string {
    return `${api}-${id}`;
  }

  /**
   * Verifica se a ordem salva ainda é válida (dentro do tempo limite)
   */
  private isOrderValid(): boolean {
    if (this.orderTimestamp.value === 0 || this.dividasOrder.value.length === 0) {
      return false;
    }
    const timeDiff = Date.now() - this.orderTimestamp.value;
    return timeDiff <= this.VALIDITY_DURATION;
  }

  /**
   * Helper para salvar no sessionStorage com tratamento de erro
   */
  private static salvarNoSessionStorage(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Erro ao salvar no sessionStorage:', error);
    }
  }

  /**
   * Helper para remover do sessionStorage com tratamento de erro
   */
  private static removerDoSessionStorage(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Erro ao remover do sessionStorage:', error);
    }
  }

  /**
   * Lê o cache de lotes do sessionStorage ou useStorage
   */
  private lerCacheLotes(): LotesOrdenacaoCache | null {
    const keys = this.getUserSpecificKeys();
    const saved = sessionStorage.getItem(keys.lotesKey);
    const parsed = parseJsonFromStorage<LotesOrdenacaoCache>(saved);
    return parsed || this.lotesOrdenacaoCache.value;
  }

  /**
   * Obtém o cache de lotes de ordenação do usuário atual
   */
  getLotesOrdenacaoCache(): LotesOrdenacaoCache | null {
    this.ensureStorageReferences();
    return this.lerCacheLotes();
  }

  /**
   * Verifica se o cache de lotes ainda é válido (dentro do tempo limite)
   */
  private isLotesCacheValid(): boolean {
    const cache = this.lerCacheLotes();
    if (!cache) {
      return false;
    }

    const timeDiff = Date.now() - cache.timestamp;
    return timeDiff <= this.VALIDITY_DURATION;
  }

  /**
   * Reseta a ordem se ela não for mais válida
   * @returns true se a ordem foi resetada, false caso contrário
   */
  resetIfExpired(): boolean {
    const wasReset = !this.isOrderValid();
    const wasLotesReset = !this.isLotesCacheValid();

    if (wasReset) {
      this.dividasOrder.value = [];
      this.orderTimestamp.value = 0;
      const keys = this.getUserSpecificKeys();
      DividasOrderingService.salvarNoSessionStorage(
        keys.orderKey,
        JSON.stringify(this.dividasOrder.value),
      );
      DividasOrderingService.salvarNoSessionStorage(
        keys.timestampKey,
        JSON.stringify(this.orderTimestamp.value),
      );
    }

    if (wasLotesReset) {
      this.lotesOrdenacaoCache.value = null;
      const keys = this.getUserSpecificKeys();
      DividasOrderingService.removerDoSessionStorage(keys.lotesKey);
    }

    return wasReset || wasLotesReset;
  }

  /**
   * Adiciona dívidas na ordem (primeira vez salva a ordem inicial, depois concatena no final)
   * @param dividas Array de dívidas para adicionar na ordem
   * @deprecated Use salvarLotesOrdenacao() para suporte a múltiplos lotes
   */
  adicionarDividasNaOrdem(dividas: DividaResource[]): void {
    if (dividas.length === 0) {
      return;
    }

    this.ensureStorageReferences();
    this.resetIfExpired();

    const novasKeys = dividas.map((d) => DividasOrderingService.calcularKey(d.api, d.id));
    const ordemAtual = Array.isArray(this.dividasOrder.value) ? this.dividasOrder.value : [];
    this.dividasOrder.value = [...ordemAtual, ...novasKeys];
    this.orderTimestamp.value = Date.now();

    const keys = this.getUserSpecificKeys();
    DividasOrderingService.salvarNoSessionStorage(
      keys.orderKey,
      JSON.stringify(this.dividasOrder.value),
    );
    DividasOrderingService.salvarNoSessionStorage(
      keys.timestampKey,
      JSON.stringify(this.orderTimestamp.value),
    );
  }

  /**
   * Salva lotes com suas ordenações no cache
   * @param lotes Array de arrays de dívidas (cada array representa um lote)
   * @param tiposOrdenacao Array de tipos de ordenação (um por lote)
   */
  salvarLotesOrdenacao(
    lotes: DividaResource[][],
    tiposOrdenacao: TipoOrdenacao[],
  ): void {
    if (lotes.length === 0 || lotes.length !== tiposOrdenacao.length) {
      return;
    }

    this.ensureStorageReferences();
    this.resetIfExpired();

    const cacheValue: LotesOrdenacaoCache = {
      lotes: lotes.map((lote) => lote.map(
        (d) => DividasOrderingService.calcularKey(d.api, d.id),
      )),
      tiposOrdenacao,
      timestamp: Date.now(),
    };

    const keys = this.getUserSpecificKeys();
    const serialized = JSON.stringify(cacheValue);
    DividasOrderingService.salvarNoSessionStorage(keys.lotesKey, serialized);

    // Verifica se foi salvo corretamente e tenta novamente se necessário
    const saved = sessionStorage.getItem(keys.lotesKey);
    if (!parseJsonFromStorage(saved)) {
      DividasOrderingService.salvarNoSessionStorage(keys.lotesKey, serialized);
    }
  }

  /**
   * Carrega e ordena dívidas usando lotes do cache
   * @param dividasParaOrdenar Array de todas as dívidas disponíveis
   * @returns Array ordenado conforme lotes do cache, ou null se cache inválido
   */
  ordenarUsandoLotesCache(dividasParaOrdenar: DividaResource[]): DividaResource[] | null {
    this.ensureStorageReferences();
    this.resetIfExpired();

    const cache = this.lerCacheLotes();
    if (!cache?.lotes?.length) {
      return null;
    }

    const timeDiff = Date.now() - cache.timestamp;
    if (timeDiff > this.VALIDITY_DURATION) {
      return null;
    }

    const dividasMap = new Map(
      dividasParaOrdenar.map((d) => [DividasOrderingService.calcularKey(d.api, d.id), d]),
    );

    const lotesReconstruidos = cache.lotes.map((loteKeys) => loteKeys
      .map((key) => dividasMap.get(key))
      .filter((d): d is DividaResource => d !== undefined));

    if (lotesReconstruidos.every((lote) => lote.length === 0)) {
      return null;
    }

    return this.getOrderingContext().ordenarLotes(lotesReconstruidos, cache.tiposOrdenacao);
  }

  /**
   * Ordena múltiplos lotes, cada um com seu tipo de ordenação
   * @param lotes Array de arrays de dívidas (cada array representa um lote)
   * @param tiposOrdenacao Array de tipos de ordenação (um por lote, na mesma ordem)
   * @returns Array único de dívidas ordenado e concatenado
   */
  ordenarLotes(
    lotes: DividaResource[][],
    tiposOrdenacao: TipoOrdenacao[],
  ): DividaResource[] {
    return this.getOrderingContext().ordenarLotes(lotes, tiposOrdenacao);
  }

  /**
   * Obtém a ordem de dívidas salva para o usuário atual
   */
  getDividasOrder(): string[] {
    this.ensureStorageReferences();
    return Array.isArray(this.dividasOrder.value) ? this.dividasOrder.value : [];
  }

  /**
   * Ordena as dívidas conforme a ordem salva na sessionStorage
   * @param dividasParaOrdenar Array de dívidas para ordenar
   * @returns Array de dívidas ordenado
   * @deprecated Use ordenarLotes() com TipoOrdenacao.ORDEM_SALVA para suporte a múltiplos lotes
   */
  ordenarDividas(dividasParaOrdenar: DividaResource[]): DividaResource[] {
    // Mantido para compatibilidade - converte array único em lote
    return this.ordenarLotes([dividasParaOrdenar], [TipoOrdenacao.ORDEM_SALVA]);
  }

  /**
   * Ordena as dívidas por uma lista pré-definida de credores (maior receita)
   * @param dividasParaOrdenar Array de dívidas para ordenar
   * @returns Array de dívidas ordenado
   * @deprecated Use ordenarLotes() com TipoOrdenacao.MAIOR_RECEITA para suporte a múltiplos lotes
   */
  ordenarDividasCredoresMaiorReceita(
    dividasParaOrdenar: DividaResource[],
  ): DividaResource[] {
    // Mantido para compatibilidade - converte array único em lote
    return this.ordenarLotes([dividasParaOrdenar], [TipoOrdenacao.MAIOR_RECEITA]);
  }

  /**
   * Limpa a ordem salva na sessionStorage
   */
  limparOrdem(): void {
    this.ensureStorageReferences();
    this.dividasOrder.value = [];
    this.orderTimestamp.value = 0;
    this.lotesOrdenacaoCache.value = null;

    const keys = this.getUserSpecificKeys();
    DividasOrderingService.removerDoSessionStorage(keys.lotesKey);
  }

  /**
   * Limpa todas as ordens de todos os usuários (para uso no logout)
   */
  limparTodasAsOrdens(): void {
    try {
      const keysToRemove = Array.from(
        { length: sessionStorage.length },
        (_, i) => sessionStorage.key(i),
      )
        .filter((key): key is string => !!key && (
          key.startsWith('dividas-order') || key.startsWith('dividas-order-timestamp')
        ));

      keysToRemove.forEach((key) => DividasOrderingService.removerDoSessionStorage(key));
      this.dividasOrder.value = [];
      this.orderTimestamp.value = 0;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Erro ao limpar todas as ordens:', error);
    }
  }

  /**
   * Verifica se a ordem atual ainda é válida (método público para testes)
   */
  verificarValidadeOrdem(): boolean {
    this.ensureStorageReferences();
    return this.isOrderValid();
  }

  /**
   * Força a reinicialização das referências de storage
   * Útil quando há mudança de usuário
   */
  reinicializarParaNovoUsuario(): void {
    this.currentUserHash = null;
    this.ensureStorageReferences();
  }
}

let instance: DividasOrderingService | null = null;

export function getDividasOrderingService(): DividasOrderingService {
  if (!instance) {
    instance = new DividasOrderingService();
  }
  return instance;
}
